import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from 'api/apiClient/apiClient'
import { getApiErrorReasons } from 'api/apiClient/apiErrorReasons.helper'
import { DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS } from 'api/dataset/dataset.constants'
import { OptJobInputData, OptJobType } from 'api/optJobs/optJobs.api'
import { DefaultPageNumberPagination } from 'api/types'
import { getErrorStatus, getQueryRetryDelay, queryClient, shouldRetryQuery } from 'helpers/queryClient'
import type { TerminalErrorAlarmMeta } from 'helpers/queryClient.types'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Datetime from 'utils/datetime/datetime'
import {
  classifySettingSaveResult,
  classifySettingSaveResults,
  getSettingSaveRefusalMessage,
} from './digitalTwin.error.helper'
import type {
  DeviationSettingLevel,
  DeviationSettingValue,
  DigitalTwinSetting,
  DigitalTwinSettingCreationResult,
  DigitalTwinTimeSeriesSetting,
  SettingSource,
  TimeSeriesSettingsCreationResult,
} from './digitalTwin.types'
import {
  BASE_SETTING_PRIORITIES,
  DEVIATION_SETTING_PRIORITIES,
  DIGITAL_TWIN_REPLICATION_RETRY_ATTEMPTS,
  DIGITAL_TWIN_REPLICATION_RETRY_DELAY_MS,
  DIGITAL_TWIN_REPLICATION_RETRY_JITTER_RATIO,
  DIGITAL_TWIN_REPLICATION_TIMEOUT_ISSUE_TYPE,
} from './digitalTwinConstants'

export type DigitalTwinTranslations = { [key: string]: string }

export type DigitalTwinSettingMetadata = {
  level: DeviationSettingLevel
  name: string
  attribute: string
  source?: SettingSource
  created_at?: ISODateTime
  priority?: number
  value?: number | null
  start_time?: ISODateTime
  end_time?: ISODateTime
  is_time_series?: boolean
  excluded_priority_layers?: number[]
}

export type TranslatedDeviationSetting = Omit<DigitalTwinSetting, 'value'> & { value?: DeviationSettingValue | string }

export type DigitalTwinSettingTarget = {
  level: DeviationSettingLevel
  name: string
  attribute: string
}

export type ConfigurableProperty = DigitalTwinSettingTarget & {
  display_name?: string
  group?: string
  min_value?: number | null
  max_value?: number | null
  measurement_unit?: string
  data_type?: string
  tags?: string[]
  available_priorities?: number[]
  default_value?: number | null
  time_series_regex_filter?: string
  write?: boolean
}

export type AccumulatorInfo = {
  unit_name: string
  start_level_base_setting_value: number
  end_level_base_setting_value: number
  start_level_deviation_setting_value: number
  end_level_deviation_setting_value: number
  start_level_meas_value: number
  start_min_level?: number
  start_max_level?: number
  end_min_level?: number
  end_max_level?: number
}

export type DigitalTwinPropertyObject = {
  name: string
  level: DeviationSettingLevel
  attribute: string
  display_name?: string
  tags?: string[]
  configurable_properties?: ConfigurableProperty[]
  measurement_unit?: string | undefined
  available_priorities?: number[]
  write?: boolean
}

export type Balance = DigitalTwinPropertyObject & {
  add_to_demand: number
  commodity: string
}

export type Exchange = DigitalTwinPropertyObject & {
  balance_to: string
  balance_from: string
  max_transfer: number
}

export type Commodity = DigitalTwinPropertyObject & {
  byproducts: DigitalTwinPropertyObject[]
  is_el: boolean
  cost: number
}

export type DigitalTwinUnit = DigitalTwinPropertyObject & {
  inputs?: { [name: string]: { [attribute: string]: string | number } }
  outputs?: { [name: string]: { [attribute: string]: string | number } }
} & {
  [attribute: string]: number | string | (DigitalTwinNode | DigitalTwinUnit | ConfigurableProperty)[]
}

export type DigitalTwinNode = DigitalTwinPropertyObject & {
  units?: DigitalTwinUnit[]
} & {
  [attribute: string]: number | string | (DigitalTwinNode | DigitalTwinUnit | ConfigurableProperty)[]
}

export type DigitalTwinModel = DigitalTwinPropertyObject & {
  nodes: DigitalTwinNode[]
  balances: Balance[]
  exchanges: Exchange[]
  commodities: Commodity[]
  tag_sorting_order?: string[]
  hidden_default_tags?: string[]
  all_tags?: string[]
  all_tags_dict?: { [key: string]: string[] }
  all_configurable_tags?: string[]
  all_configurable_tags_dict?: { [key: string]: string[] }
  show_model_node?: boolean
  build_metadata?: {
    build_user?: string
    git_sha?: string
  }
}

export type DigitalTwin = {
  uid: number
  id: number
  name: string
  display_name: string
  version: number
  description?: string
  created_at: ISODateTime
  updated_at: ISODateTime
  model: DigitalTwinModel
  created_by?: number
  system?: number
  layout?: DigitalTwinLayout
  translations?: DigitalTwinTranslations
}

export type DigitalTwinLayout = { [name: string]: { x: number; y: number } }

/**
 * A uid is handed to the client by the backend, so a 404 means the row has not yet
 * replicated to the read replica this request hit — a transient condition, not a
 * genuinely missing resource, so 404s are retried over the replication window.
 */
export function shouldRetryDigitalTwinQuery(failureCount: number, error: unknown): boolean {
  if (getErrorStatus(error) === 404) {
    return failureCount < DIGITAL_TWIN_REPLICATION_RETRY_ATTEMPTS
  }

  return shouldRetryQuery(failureCount, error)
}

/**
 * 404s use a near-fixed delay so the total retry span matches the expected replication
 * window rather than shrinking under exponential backoff.
 */
export function getDigitalTwinRetryDelay(attemptIndex: number, error: unknown): number {
  if (getErrorStatus(error) === 404) {
    // Replica lag needs a predictable retry window; jitter reduces synchronized retries
    // without changing that window shape to exponential backoff.
    const jitterSpread = DIGITAL_TWIN_REPLICATION_RETRY_JITTER_RATIO * 2
    const jitterMultiplier = 1 - DIGITAL_TWIN_REPLICATION_RETRY_JITTER_RATIO + Math.random() * jitterSpread

    return Math.round(DIGITAL_TWIN_REPLICATION_RETRY_DELAY_MS * jitterMultiplier)
  }

  return getQueryRetryDelay(attemptIndex)
}

export function useDigitalTwin(uid?: number, include_inherited_tags = true, include_all_tags = true) {
  return useQuery({
    queryKey: ['get_digital_twin', uid, include_inherited_tags, include_all_tags],

    queryFn: async () => {
      const params: { include_inherited_tags?: number; include_all_tags?: number } = {}
      if (include_inherited_tags) {
        params.include_inherited_tags = 1
      }
      if (include_all_tags) {
        params.include_all_tags = 1
      }
      return await apiClient<DigitalTwin>(`digital_twin/${uid}`, { params })
    },

    enabled: uid !== null && uid !== undefined,

    retry: shouldRetryDigitalTwinQuery,
    retryDelay: getDigitalTwinRetryDelay,
    meta: { terminalErrorIssueType: DIGITAL_TWIN_REPLICATION_TIMEOUT_ISSUE_TYPE } satisfies TerminalErrorAlarmMeta,
  })
}

export function useAllDigitalTwins(systemId: number | undefined, pageNumber: number, include_inherited_tags?: boolean) {
  return useQuery({
    queryKey: ['list_all_latest_digital_twins', systemId, include_inherited_tags, pageNumber],

    queryFn: async () => {
      if (systemId === null || systemId === undefined) {
        return { next: null, previous: null, results: [] }
      }

      const params: { page: number; system_id?: number; include_inherited_tags?: string } = {
        page: pageNumber,
        system_id: systemId,
      }

      if (include_inherited_tags) {
        params['include_inherited_tags'] = '1'
      }

      return await apiClient<DefaultPageNumberPagination<DigitalTwin[]>>('digital_twins', { params })
    },
  })
}

export function useDigitalTwinVersions(uid?: number, limit?: number) {
  return useQuery({
    queryKey: ['get_digital_twin_versions', uid, limit],

    queryFn: async () => {
      const params = limit !== undefined ? { limit: limit } : {}

      return await apiClient<DigitalTwin[]>(`digital_twin/${uid}/versions`, { params })
    },
  })
}

export type DigitalTwinUpdateRequest = {
  name: string
  display_name?: string
  model: DigitalTwinModel | Record<string, never> //django requires this to exist when sending post (but is okay with an empty object)
  layout?: DigitalTwinLayout
  description?: string
}

export type UpdateDigitalTwinParams = {
  uid: number
  data: DigitalTwinUpdateRequest
}

export function useUpdateDigitalTwin() {
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ uid, data }: UpdateDigitalTwinParams) =>
      apiClient<DigitalTwin>(`digital_twin/${uid}/update`, { method: 'PUT', data: data }),

    onError: () => {
      alertStore.error(t('Failed to save digital twin'))
    },

    onSuccess: () => {
      alertStore.success(t('Digital twin saved successfully'))
    },
  })
}

export type DigitalCreationRequest = DigitalTwinUpdateRequest & {
  system_id?: number
}

export type CreateDigitalTwinParams = {
  data: DigitalCreationRequest
}

export function useCreateDigitalTwin() {
  return useMutation({
    mutationFn: ({ data }: CreateDigitalTwinParams) => apiClient<DigitalTwin>('digital_twin', { method: 'POST', data }),
  })
}

export type DigitalTwinOptJobCreationRequest = {
  opt_job_type: OptJobType
  input_data: OptJobInputData
}

export type CreateDigitalTwinOptJobParams = {
  uid: number
  data: DigitalTwinOptJobCreationRequest
}

export function useCreateDigitalTwinOptJob() {
  return useMutation({
    mutationFn: ({ uid, data }: CreateDigitalTwinOptJobParams) =>
      apiClient<DigitalTwin>(`digital_twin/${uid}/queue_opt_job`, { method: 'POST', data }),
  })
}

export function useConfigurablePropertys(uid?: number) {
  return useQuery({
    queryKey: ['get_configurable_digital_twin_properties', uid],

    queryFn: async () => {
      if (!uid) {
        return []
      }

      return await apiClient<ConfigurableProperty[]>(`digital_twin/${uid}/configurable_properties`)
    },
  })
}

export type FetchSettingsParams = {
  digitalTwinUid?: number
  properties?: DigitalTwinSettingTarget[]
  priorities?: SystemSettingPriorityLevel[]
  activeFrom?: ISODateTime | null
  activeTo?: ISODateTime | null
  usePropertyFiltersList?: boolean
}

export function useDigitalTwinSettings() {
  const { activeSystem } = useAuth()

  return useMutation({
    mutationFn: async ({
      digitalTwinUid,
      properties,
      priorities,
      activeFrom,
      activeTo,
      usePropertyFiltersList,
    }: FetchSettingsParams): Promise<DigitalTwinSetting[]> => {
      const uid = digitalTwinUid ?? activeSystem?.primary_digital_twin?.uid
      if (!uid) {
        return Promise.resolve([])
      }

      const propertyFilters =
        properties
          ?.filter((property) => property.level && property.name && property.attribute)
          ?.map((property) => ({
            level: property.level,
            name: property.name,
            attribute: property.attribute,
          })) ?? []

      const params: {
        priority?: number[]
        start_time?: string
        end_time?: string
        use_property_filters_list?: boolean
      } = {}

      if (activeFrom) {
        params['start_time'] = activeFrom
      }

      if (activeTo) {
        params['end_time'] = activeTo
      }

      if (priorities) {
        params['priority'] = priorities.map((priority) => priority.priority_level)
      }

      if (usePropertyFiltersList) {
        params['use_property_filters_list'] = usePropertyFiltersList
      }

      const [result1, result2] = await Promise.allSettled([
        apiClient<DigitalTwinSetting[]>(`digital_twin/${uid}/settings`, {
          method: 'POST',
          data: { property_filters: propertyFilters },
          params,
          addBracketsToArrayParamKeys: false,
        }),
        apiClient<DigitalTwinTimeSeriesSetting[]>(`digital_twin/${uid}/time_series_settings`, {
          method: 'POST',
          data: { property_filters: propertyFilters },
          params,
          addBracketsToArrayParamKeys: false,
        }),
      ]).then((results) => {
        return results.map((result) => (result.status === 'fulfilled' ? result.value : []))
      })

      const settingResult1 = result1 || []
      const settingResult2 = result2 || []
      const settingsToReturn: DigitalTwinSetting[] = [...settingResult1, ...settingResult2].map((setting_data) => {
        if (isDigitalTwinSetting(setting_data)) {
          return setting_data
        } else if (isDigitalTwinTimeSeriesSetting(setting_data)) {
          return timeSeriesSettingToDigitalTwinSetting(setting_data)
        } else {
          throw new Error('Unexpected type for time_series')
        }
      })

      return settingsToReturn
    },
  })
}

// Type guard to check if the object is a DigitalTwinSetting
function isDigitalTwinSetting(obj: any): obj is DigitalTwinSetting {
  return 'value' in obj && 'time_series_setting' in obj
}

// Type guard to check if the object is a DigitalTwinTimeSeriesSetting
function isDigitalTwinTimeSeriesSetting(obj: any): obj is DigitalTwinTimeSeriesSetting {
  return 'settings' in obj && Array.isArray(obj.settings)
}

export type FetchSettingMetadataParams = {
  digitalTwinUid?: number
  startTime?: ISODateTime | null
  endTime?: ISODateTime | null
  startFrom?: ISODateTime | null
  priorities?: SystemSettingPriorityLevel[]
}

function getDigitalTwinSettingsMetadataParams({
  startTime,
  endTime,
  startFrom,
  priorities,
}: FetchSettingMetadataParams): {
  priority?: number[]
  start_time?: string
  end_time?: string
} {
  const params: {
    start_time?: string
    end_time?: string
    start_from?: string
    priority?: number[]
  } = {}

  if (startTime) {
    params['start_time'] = startTime
  }

  if (endTime) {
    params['end_time'] = endTime
  }

  if (startFrom) {
    params['start_from'] = startFrom
  }

  if (priorities) {
    params['priority'] = priorities.map((priority) => priority.priority_level)
  }

  return params
}

export function useDigitalTwinSettingsCreatedAtMetadata() {
  const { activeSystem } = useAuth()

  return useMutation({
    mutationFn: (metadataParams: FetchSettingMetadataParams) => {
      const uid = metadataParams.digitalTwinUid ?? activeSystem?.primary_digital_twin?.uid
      if (!uid) {
        return Promise.resolve([])
      }

      const params = getDigitalTwinSettingsMetadataParams(metadataParams)
      return apiClient<DigitalTwinSettingMetadata[]>(`digital_twin/${uid}/settings/metadata/created_at`, {
        method: 'GET',
        params,
        addBracketsToArrayParamKeys: false,
      })
    },
  })
}

export function useDigitalTwinSettingsActiveMetadata() {
  const { activeSystem } = useAuth()

  return useMutation({
    mutationFn: (metadataParams: FetchSettingMetadataParams) => {
      const uid = metadataParams.digitalTwinUid ?? activeSystem?.primary_digital_twin?.uid
      if (!uid) {
        return Promise.resolve([])
      }

      const params = getDigitalTwinSettingsMetadataParams(metadataParams)
      return apiClient<DigitalTwinSettingMetadata[]>(`digital_twin/${uid}/settings/metadata/active`, {
        method: 'GET',
        params,
        addBracketsToArrayParamKeys: false,
      })
    },
  })
}

type SettingCreationRequestBase = {
  level: DeviationSettingLevel
  name: string // object name / target, e.g. "p1"
  attribute: string // property name, e.g. "availability"
  comment: string
  priority: number
}

export type DigitalTwinSettingCreationRequest = SettingCreationRequestBase & {
  start_time: ISODateTime | null
  end_time: ISODateTime | null
  value?: number | null | TimeSeriesSettingValue
  deleted_at?: ISODateTime | null
  isTimeSeries: boolean
  unit: string
  isBaseSetting: boolean
  id?: number | undefined
  creator_name?: string
  source?: SettingSource
  updated_at?: ISODateTime | null
  operational_event?: number | null
  excluded_priority_layers?: number[]
}

export type CreateDigitalTwinSettingParams = {
  overwrite: boolean
  digitalTwinUid: number
  data: DigitalTwinSettingCreationRequest
}

export function setSettingRequestMinutes(
  requestData: DigitalTwinSettingCreationRequest | TimeSeriesSettingCreationRequest
) {
  // When modifying settings, we sometimes set the minutes of the end time to 59 for presentation purposes.
  // The backend expects all times to be on the hour, so we need to reset the minutes to 00.
  // A setting "00:00 - 23:59" is "00:00 - 23:00" in the backend.
  const newStartTime = requestData.start_time === null ? null : Datetime.ISODatetimeTo00Minutes(requestData.start_time)
  const newEndTime = requestData.end_time === null ? null : Datetime.ISODatetimeTo00Minutes(requestData.end_time)
  return { ...requestData, start_time: newStartTime, end_time: newEndTime }
}

export function useCreateDigitalTwinSetting() {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({ digitalTwinUid, data, overwrite }: CreateDigitalTwinSettingParams) => {
      return apiClient<DigitalTwinSettingCreationResult>(`digital_twin/${digitalTwinUid}/setting`, {
        method: 'POST',
        data: setSettingRequestMinutes(data),
        params: { overwrite: overwrite ? 1 : 1 },
      })
    },

    onSuccess: (data, params) => {
      const outcome = classifySettingSaveResult(data)
      if (outcome.kind === 'saved') {
        queryClient.invalidateQueries({ queryKey: [GET_DIGITAL_TWIN_ACCUMULATOR_INFO_QUERY_KEY] })
        queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS] })
        if (!params.data.isBaseSetting) {
          alertStore.success(t('Deviation setting created successfully'))
        } else {
          alertStore.success(t('Base setting created successfully'))
        }
      } else {
        alertStore.error(getSettingSaveRefusalMessage(outcome, t('Failed to create setting.'), t))
      }
    },

    onError: (error) => {
      if (Array.isArray(error) && error.length > 0) {
        alertStore.error(t('Failed to create setting.') + ' ' + t(error[0]))
      } else {
        alertStore.error(t('Failed to create setting.'))
      }
    },
  })
}

export type TimeSeriesSettingValue = { [time: ISODateTime]: { id?: number; value: number | null } }

export type TimeSeriesSettingCreationRequest = SettingCreationRequestBase & {
  start_time: ISODateTime
  end_time: ISODateTime
  value: TimeSeriesSettingValue
  excluded_priority_layers?: number[]
  deleted_at?: ISODateTime | null
  creator_name?: string
  source?: SettingSource
  updated_at?: ISODateTime | null
}

export type CreateTimeSeriesSettingParams = {
  digitalTwinUid: number
  data: TimeSeriesSettingCreationRequest
  splitOnNullValues: boolean
}

export function splitTimeSeriesSettingOnNullValues(
  request: TimeSeriesSettingCreationRequest
): TimeSeriesSettingCreationRequest[] {
  const times = Datetime.getHoursBetween(request.start_time, request.end_time)
  const newRequests: TimeSeriesSettingCreationRequest[] = []
  let currentStartTime: ISODateTime | null = null
  let currentValues: { [time: ISODateTime]: { id?: number; value: number | null } } = {}

  for (let i = 0; i < times.length; i++) {
    const time = times[i]
    const valueObject = request.value[time]

    if (valueObject && valueObject.value !== null) {
      if (currentStartTime === null) {
        currentStartTime = time
      }
      currentValues[time] = valueObject
    } else if (currentStartTime !== null) {
      const newRequest: TimeSeriesSettingCreationRequest = {
        start_time: currentStartTime,
        end_time: times[i - 1],
        value: currentValues,
        excluded_priority_layers: request.excluded_priority_layers,
        deleted_at: request.deleted_at,
        level: request.level,
        name: request.name,
        attribute: request.attribute,
        comment: request.comment,
        priority: request.priority,
      }
      newRequests.push(newRequest)
      currentStartTime = null
      currentValues = {}
    }
  }

  if (currentStartTime !== null) {
    const newRequest: TimeSeriesSettingCreationRequest = {
      start_time: currentStartTime,
      end_time: times[times.length - 1],
      value: currentValues,
      excluded_priority_layers: request.excluded_priority_layers,
      deleted_at: request.deleted_at,
      level: request.level,
      name: request.name,
      attribute: request.attribute,
      comment: request.comment,
      priority: request.priority,
      creator_name: request.creator_name,
      updated_at: request.updated_at,
    }
    newRequests.push(newRequest)
  }

  return newRequests
}

/** A time series setting in the shape the settings lists use: one setting whose value is its hours. */
export function timeSeriesSettingToDigitalTwinSetting(
  timeSeriesSetting: DigitalTwinTimeSeriesSetting
): DigitalTwinSetting {
  const setting = {
    id: timeSeriesSetting.id,
    digital_twin_id: timeSeriesSetting.digital_twin_id,
    level: timeSeriesSetting.level,
    name: timeSeriesSetting.name,
    attribute: timeSeriesSetting.attribute,
    created_at: timeSeriesSetting.created_at,
    updated_at: timeSeriesSetting.updated_at ?? timeSeriesSetting.created_at,
    start_time: timeSeriesSetting.start_time,
    end_time: timeSeriesSetting.end_time,
    comment: timeSeriesSetting.comment,
    creator_name: timeSeriesSetting.creator_name,
    priority: timeSeriesSetting.priority,
    excluded_priority_layers: timeSeriesSetting.excluded_priority_layers,
    value: timeSeriesSetting.settings.map((hour) => ({
      id: hour.id,
      value: (hour.value ?? null) as number | null,
    })),
    isTimeSeries: true,
    operational_event: timeSeriesSetting.settings.length > 0 ? timeSeriesSetting.settings[0].operational_event : null,
  }
  return setting
}

/**
 * The setting a successful save stored, in the shape the settings lists use; null when it stored
 * several, as when a time series is split on its empty hours, or none.
 */
export function getSavedSetting(
  result: DigitalTwinSettingCreationResult | TimeSeriesSettingsCreationResult | TimeSeriesSettingsCreationResult[]
): DigitalTwinSetting | null {
  const results = Array.isArray(result) ? result : [result]
  if (results.length !== 1) {
    return null
  }
  const [saved] = results
  if ('time_series' in saved) {
    return saved.time_series ? timeSeriesSettingToDigitalTwinSetting(saved.time_series) : null
  }
  if ('created' in saved) {
    return saved.created ?? null
  }
  return null
}

export function useCreateTimeSeriesSetting() {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({ digitalTwinUid, data, splitOnNullValues }: CreateTimeSeriesSettingParams) => {
      let timeSeriesSettingsCreationRequests: TimeSeriesSettingCreationRequest[] = []
      if (splitOnNullValues) {
        timeSeriesSettingsCreationRequests = splitTimeSeriesSettingOnNullValues(data)
      } else {
        timeSeriesSettingsCreationRequests.push(data)
      }

      const mutationPromises = timeSeriesSettingsCreationRequests.map((request) => {
        const times = Datetime.getHoursBetween(request.start_time, request.end_time)
        const value = times.map((time) => (time in request.value ? request.value[time].value : null))

        return apiClient<TimeSeriesSettingsCreationResult>(`digital_twin/${digitalTwinUid}/time_series_setting`, {
          method: 'POST',
          data: { ...setSettingRequestMinutes(request), value },
        })
      })

      return Promise.all(mutationPromises)
    },

    onSuccess: (data) => {
      const outcome = classifySettingSaveResults(data)
      if (outcome.kind === 'saved') {
        queryClient.invalidateQueries({ queryKey: [GET_DIGITAL_TWIN_ACCUMULATOR_INFO_QUERY_KEY] })
        queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS] })
        alertStore.success(t('Setting saved.'))
      } else {
        alertStore.error(getSettingSaveRefusalMessage(outcome, t('Failed to save setting.'), t))
      }
    },

    onError: (error) => {
      if (Array.isArray(error) && error.length > 0) {
        alertStore.error(t('Failed to create setting.') + ' ' + t(error[0]))
      } else {
        alertStore.error(t('Failed to create setting.'))
      }
    },
  })
}

export type UpdateDigitalTwinSettingParams = {
  overwrite: boolean
  settingId: number
  data: DigitalTwinSettingCreationRequest
  digitalTwinUid?: number
}

export function useUpdateDigitalTwinSetting() {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({ settingId, data, overwrite }: UpdateDigitalTwinSettingParams) => {
      return apiClient<DigitalTwinSettingCreationResult>(`digital_twin/setting/${settingId}/update`, {
        method: 'PUT',
        data: setSettingRequestMinutes(data),
        params: { overwrite: overwrite ? 1 : 0 },
      })
    },

    onSuccess: (data) => {
      const outcome = classifySettingSaveResult(data)
      if (outcome.kind === 'saved') {
        queryClient.invalidateQueries({ queryKey: [GET_DIGITAL_TWIN_ACCUMULATOR_INFO_QUERY_KEY] })
        queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS] })
        alertStore.success(t('Setting saved.'))
      } else {
        alertStore.error(getSettingSaveRefusalMessage(outcome, t('Failed to save setting.'), t))
      }
    },

    onError: (error) => {
      if (Array.isArray(error) && error.length > 0) {
        alertStore.error(t('Failed to save setting.') + ' ' + t(error[0]))
      } else {
        alertStore.error(t('Failed to save setting.'))
      }
    },
  })
}

export function useUpdateTimeSeriesSetting() {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({ settingId, data, digitalTwinUid, overwrite }: UpdateDigitalTwinSettingParams) => {
      return apiClient<TimeSeriesSettingsCreationResult>(
        `digital_twin/${digitalTwinUid}/time_series_settings/${settingId}/update`,
        {
          method: 'PUT',
          data: { ...setSettingRequestMinutes(data), settings: Object.values(data.value ?? {}) },
          params: { overwrite: overwrite ? 1 : 0 },
        }
      )
    },

    onSuccess: (data) => {
      const outcome = classifySettingSaveResult(data)
      if (outcome.kind === 'saved') {
        queryClient.invalidateQueries({ queryKey: [GET_DIGITAL_TWIN_ACCUMULATOR_INFO_QUERY_KEY] })
        queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS] })
        alertStore.success(t('Setting saved.'))
      } else {
        alertStore.error(getSettingSaveRefusalMessage(outcome, t('Failed to save setting.'), t))
      }
    },

    onError: (error) => {
      const [reason] = getApiErrorReasons(error)
      alertStore.error(reason ? `${t('Failed to save setting.')} ${t(reason)}` : t('Failed to save setting.'))
    },
  })
}

export type DeleteDeviationSettingParams = {
  settingId: number
  digitalTwinUid?: number
}

export function useDeleteDeviationSetting() {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({ settingId }: DeleteDeviationSettingParams) =>
      apiClient<undefined>(`digital_twin/setting/${settingId}`, { method: 'DELETE' }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_DIGITAL_TWIN_ACCUMULATOR_INFO_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS] })
      alertStore.success(t('Setting removed.'))
    },

    onError: (error) => {
      if (Array.isArray(error) && error.length > 0) {
        alertStore.error(t('The setting could not be removed') + ': ' + t(error[0]))
      } else {
        alertStore.error(t('The setting could not be removed'))
      }
    },
  })
}

export function useDeleteTimeSeriesSetting() {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({ settingId, digitalTwinUid }: DeleteDeviationSettingParams) =>
      apiClient<undefined>(`digital_twin/${digitalTwinUid}/time_series_settings/${settingId}`, { method: 'DELETE' }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_DIGITAL_TWIN_ACCUMULATOR_INFO_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS] })
      alertStore.success(t('Setting removed.'))
    },

    onError: (error) => {
      if (Array.isArray(error) && error.length > 0) {
        alertStore.error(t('The setting could not be removed') + ': ' + t(error[0]))
      } else {
        alertStore.error(t('The setting could not be removed'))
      }
    },
  })
}

export type DeviationSettingUpdateRequest = {
  name: string
  display_name?: string
  model?: DigitalTwinModel
  layout?: DigitalTwinLayout
  description?: string
}

export type UpdateDeviationSettingTwinParams = {
  settingId: number
  data: DeviationSettingUpdateRequest
}

export const GET_DIGITAL_TWIN_ACCUMULATOR_INFO_QUERY_KEY = 'get_digital_twin_accumulator_info'
export function useDigitalTwinAckumulatorInfo(uid?: number, startTime?: string, endTime?: string) {
  return useQuery({
    queryKey: [GET_DIGITAL_TWIN_ACCUMULATOR_INFO_QUERY_KEY, uid],

    queryFn: async () => {
      if (!uid) {
        return []
      }

      return await apiClient<AccumulatorInfo[]>(`digital_twin/${uid}/accumulator_info`, {
        params: {
          start_time: startTime,
          end_time: endTime,
          priority: [
            ...DEVIATION_SETTING_PRIORITIES.map((level) => level.priority_level),
            ...BASE_SETTING_PRIORITIES.map((level) => level.priority_level),
          ],
        },
      })
    },
  })
}

const DIGITAL_TWIN_SETTINGS_QUERY_KEY = 'digital_twin_settings'

export function useOutdatedPropertiesWithSettings(uid?: number) {
  return useQuery({
    queryKey: [DIGITAL_TWIN_SETTINGS_QUERY_KEY, uid],

    queryFn: async () => {
      if (!uid) {
        return []
      }

      return await apiClient<{ level: string; name: string; attribute: string; count: number }[]>(
        `digital_twin/${uid}/outdated_configurable_properties`
      )
    },
  })
}

type BulkDeleteSettingsParams = {
  level: string
  name: string
  attribute: string
  digitalTwinUid: number
}

export function useBulkDeleteSettings() {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({ digitalTwinUid, level, name, attribute }: BulkDeleteSettingsParams) =>
      apiClient<{ deleted: number }>(`digital_twin/${digitalTwinUid}/settings/bulk_delete`, {
        method: 'DELETE',
        params: {
          level,
          name,
          attribute,
        },
      }),

    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [DIGITAL_TWIN_SETTINGS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS] })
      alertStore.success(t('{{count}} settings removed', { count: result?.deleted || 0 }))
    },

    onError: () => {
      alertStore.error(t('Failed to remove settings'))
    },
  })
}

type BulkUpdateSettingPropertiesParams = {
  digitalTwinUid: number
  from: {
    level: string
    name: string
    attribute: string
  }
  to: {
    level: string
    name: string
    attribute: string
  }
}

export function useBulkUpdateSettingProperties() {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({ digitalTwinUid, from, to }: BulkUpdateSettingPropertiesParams) =>
      apiClient<{ updated_settings: number }>(`digital_twin/${digitalTwinUid}/settings/bulk_update_properties`, {
        method: 'PATCH',
        data: { migrate_from: from, migrate_to: to },
      }),

    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [DIGITAL_TWIN_SETTINGS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS] })
      if (result == null || typeof result.updated_settings !== 'number') {
        alertStore.error(t('Failed to update settings: unexpected response'))
        return
      }
      alertStore.success(t('{{count}} settings updated', { count: result.updated_settings }))
    },

    onError: (error: { body?: string }) => {
      alertStore.error(t(error.body ?? 'Failed to update settings'))
    },
  })
}
